import { useMemo } from 'react';
import { Alert, Button, Empty, Popconfirm, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined } from '@ant-design/icons';
import type { DoohMedia } from '@goelectric/shared-types';
import { MediaKindBadge } from './DoohBadges';
import {
  formatBytes,
  formatChecksum,
  formatDateTime,
  formatDuration,
  formatResolution,
} from '../utils/format';

export interface MediaLibraryTableProps {
  media: DoohMedia[];
  loading: boolean;
  error: string | null;
  /** Habilita acciones de escritura (subir/eliminar); solo super admin / marketing. */
  canManage: boolean;
  onRetry: () => void;
  onUpload: () => void;
  onSelect: (media: DoohMedia) => void;
  onDelete: (media: DoohMedia) => void;
}

/**
 * Tabla de la biblioteca de medios DOOH (TG-58): metadatos (mime, tamaño,
 * dimensiones, checksum/estado), empty-state con acción sugerida y acciones de
 * escritura gated por rol.
 */
export function MediaLibraryTable({
  media,
  loading,
  error,
  canManage,
  onRetry,
  onUpload,
  onSelect,
  onDelete,
}: MediaLibraryTableProps) {
  const columns = useMemo<ColumnsType<DoohMedia>>(
    () => [
      {
        title: 'Medio',
        key: 'filename',
        fixed: 'left',
        width: 280,
        render: (_, item) => (
          <Space direction="vertical" size={2}>
            <Typography.Text strong ellipsis style={{ maxWidth: 240 }}>
              {item.filename}
            </Typography.Text>
            <Space size={6}>
              <MediaKindBadge kind={item.kind} />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {item.mime}
              </Typography.Text>
            </Space>
          </Space>
        ),
      },
      {
        title: 'Tamaño',
        dataIndex: 'sizeBytes',
        key: 'sizeBytes',
        width: 110,
        align: 'right',
        sorter: (a, b) => a.sizeBytes - b.sizeBytes,
        render: (value: number) => formatBytes(value),
      },
      {
        title: 'Dimensiones',
        key: 'dimensions',
        width: 140,
        render: (_, item) => formatResolution(item.width, item.height),
      },
      {
        title: 'Duración',
        dataIndex: 'durationSeconds',
        key: 'durationSeconds',
        width: 110,
        align: 'right',
        render: (value: number) => formatDuration(value),
      },
      {
        title: 'Suma de verificación',
        dataIndex: 'checksum',
        key: 'checksum',
        width: 150,
        render: (value: string) => (
          <Typography.Text style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }} title={value}>
            {formatChecksum(value)}
          </Typography.Text>
        ),
      },
      {
        title: 'Creado',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 180,
        render: (value: string) => formatDateTime(value),
      },
      {
        title: 'Acciones',
        key: 'actions',
        width: canManage ? 170 : 90,
        align: 'right',
        render: (_, item) => (
          <Space size={4}>
            <Button size="small" onClick={() => onSelect(item)}>
              Ver
            </Button>
            {canManage && (
              <Popconfirm
                title="Eliminar medio"
                description="Se eliminará el archivo de la biblioteca. Esta acción no se puede deshacer."
                okText="Eliminar"
                okButtonProps={{ danger: true }}
                cancelText="Cancelar"
                onConfirm={() => onDelete(item)}
              >
                <Button size="small" danger icon={<DeleteOutlined />} aria-label={`Eliminar ${item.filename}`}>
                  Eliminar
                </Button>
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ],
    [canManage, onDelete, onSelect],
  );

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudieron cargar los medios"
        description={error}
        action={
          <Button size="small" onClick={onRetry}>
            Reintentar
          </Button>
        }
      />
    );
  }

  return (
    <Table<DoohMedia>
      rowKey="id"
      size="middle"
      loading={loading}
      columns={columns}
      dataSource={media}
      scroll={{ x: 1100 }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Aún no hay medios en la biblioteca"
          >
            {canManage && (
              <Button type="primary" onClick={onUpload}>
                Subir el primer medio
              </Button>
            )}
          </Empty>
        ),
      }}
    />
  );
}
